import { property, customElement } from 'lit/decorators.js';
import { s as styles$1, b as bound, S as StripeBase } from './shared.js';
import { css } from 'lit';
import 'lit/directive.js';
import 'lit/directives/if-defined.js';
import '@typed/curry';
import '@lavadrop/kebab-case';
import '@lavadrop/camel-case';
import '@pacote/memoize';

function throwResponseError(response) {
    if (response.error)
        throw response.error;
    else
        return response;
}

const styles = css`#stripe {
  box-sizing: border-box;
  min-width: var(--stripe-payment-request-element-min-width, 300px);
  padding: var(--stripe-payment-request-element-padding, 8px 12px);
  background: var(--stripe-payment-request-element-background, white);
}
`;

var __decorate = (undefined && undefined.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (undefined && undefined.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __classPrivateFieldGet = (undefined && undefined.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (undefined && undefined.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _StripePaymentRequest_displayItems, _StripePaymentRequest_shippingOptions;
function isStripeDisplayItem(el) {
    return el.tagName.toLowerCase() === 'stripe-display-item';
}
function datasetToStripeDisplayItem({ dataset: { amount, label, pending } }) {
    return {
        label,
        amount: parseInt(amount),
        ...pending !== undefined && { pending: pending === 'true' ? true : false },
    };
}
function isStripeShippingOption(el) {
    return el.tagName.toLowerCase() === 'stripe-shipping-option';
}
function datasetToStripeShippingOption({ dataset: { amount, ...rest } }) {
    return {
        amount: parseInt(amount),
        ...rest,
    };
}
function mapDataset(el) {
    return (isStripeDisplayItem(el) ? datasetToStripeDisplayItem(el)
        : datasetToStripeShippingOption(el));
}
/**
 * Custom element wrapper for Stripe.js v3 Payment Request Buttons.
 *
 * 👨‍🎨 [Live Demo](https://bennypowers.dev/stripe-elements/?path=/docs/stripe-payment-request--enter-a-stripe-publishable-key) 👀
 *
 * ### 🧙‍♂️ Usage
 * If you prebuilt with Snowpack, load the module from your `web_modules` directory
 *
 * ```html
 * <script type="module" src="/web_modules/@power-elements/stripe-elements/stripe-payment-request.js"></script>
 * ```
 *
 * Alternatively, load the module from the unpkg CDN
 * ```html
 * <script type="module" src="https://unpkg.com/@power-elements/stripe-elements/stripe-payment-request.js?module"></script>
 * ```
 *
 * Then you can add the element to your page.
 *
 * ```html
 *
 * <stripe-payment-request id="payment-request"
 *     publishable-key="pk_test_XXXXXXXXXXXXXXXXXXXXXXXX"
 *     generate="token"
 *     action="/charges"
 *     country="CA"
 *     currency="cad"
 *     amount="1000"
 *     label="Ten Bones"
 *     request-payer-name
 *     request-payer-email
 *     request-payer-phone
 * ></stripe-payment-request>
 * ```
 *
 * See the demos for more comprehensive examples.
 *   - Using `<stripe-payment-request>` with [plain HTML and JavaScript](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-vanilla--stripe-payment-request).
 *   - Using `<stripe-payment-request>` in a [LitElement](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-litelement--stripe-payment-request).
 *   - Using `<stripe-payment-request>` in a [Vue Component](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-vue--stripe-payment-request).
 *   - Using `<stripe-payment-request>` in an [Angular component](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-angular--stripe-payment-request).
 *   - Using `<stripe-payment-request>` in a [React component](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-react--stripe-payment-request).
 *   - Using `<stripe-payment-request>` in a [Preact component](https://bennypowers.dev/stripe-elements/?path=/docs/framework-examples-preact--stripe-payment-request).
 *
 * @cssprop [--stripe-payment-request-element-min-width=`300px`] - min-width property of the container element
 * @cssprop [--stripe-payment-request-element-padding=`8px 12px`] - padding property of the container element
 * @cssprop [--stripe-payment-request-element-background=`white`] - background property of the container element
 *
 * @element stripe-payment-request
 * @extends StripeBase
 *
 * @fires 'unsupported' - When the element detects that the user agent cannot make a payment
 * @fires 'fail' - When a payment request fails
 * @fires 'cancel' - When a payment request is cancelled
 * @fires 'shippingaddresschange' - When the user chooses a different shipping address
 * @fires 'shippingoptionchange' - When the user chooses a different shipping option
 */
let StripePaymentRequest = class StripePaymentRequest extends StripeBase {
    constructor() {
        super(...arguments);
        /**
         * Whether or not the device can make the payment request.
         * @readonly
         */
        this.canMakePayment = null;
        _StripePaymentRequest_displayItems.set(this, void 0);
        /**
         * Stripe PaymentIntent
         */
        this.paymentIntent = null;
        /**
         * Stripe PaymentRequest
         */
        this.paymentRequest = null;
        /**
         * If you might change the payment amount later (for example, after you have calcluated shipping costs), set this to true. Note that browsers treat this as a hint for how to display things, and not necessarily as something that will prevent submission.
         */
        this.pending = false;
        _StripePaymentRequest_shippingOptions.set(this, void 0);
        this.buttonType = 'default';
        this.buttonTheme = 'dark';
    }
    /**
     * An array of DisplayItem objects. These objects are shown as line items in the browser’s payment interface. Note that the sum of the line item amounts does not need to add up to the total amount above.
     */
    get displayItems() {
        const value = __classPrivateFieldGet(this, _StripePaymentRequest_displayItems, "f");
        return (Array.isArray(value) ? value
            : this.parseDatasets('stripe-display-item'));
    }
    set displayItems(value) {
        const oldValue = this.displayItems;
        __classPrivateFieldSet(this, _StripePaymentRequest_displayItems, value, "f");
        this.requestUpdate('displayItems', oldValue);
    }
    /**
     * An array of ShippingOption objects. The first shipping option listed appears in the browser payment interface as the default option.
     */
    get shippingOptions() {
        const value = __classPrivateFieldGet(this, _StripePaymentRequest_shippingOptions, "f");
        return Array.isArray(value) ? value : this.parseDatasets('stripe-shipping-option');
    }
    set shippingOptions(value) {
        const oldValue = this.shippingOptions;
        __classPrivateFieldSet(this, _StripePaymentRequest_shippingOptions, value, "f");
        this.requestUpdate('shippingOptions', oldValue);
    }
    /* PUBLIC API */
    reset() {
        super.reset();
        this.setReadOnlyProperties({ paymentIntent: null });
    }
    /* LIFECYCLE */
    /** @inheritdoc */
    updated(changed) {
        super.updated(changed);
        if (changed.has('generate'))
            this.initPaymentRequestListeners();
        if (changed.has('amount'))
            this.updatePaymentRequest();
    }
    /* PRIVATE API */
    /**
     * Creates a StripePaymentRequestOptions object.
     */
    getStripePaymentRequestOptions() {
        const { country, currency, displayItems, shippingOptions, requestShipping, requestPayerEmail, requestPayerName, requestPayerPhone, label = '', amount = 0, } = this;
        const total = { label, amount };
        return {
            country,
            currency,
            displayItems,
            requestPayerEmail,
            requestPayerName,
            requestPayerPhone,
            requestShipping,
            shippingOptions,
            total,
        };
    }
    /**
     * Initializes the PaymentRequest Object.
     */
    async initElement() {
        await this.initPaymentRequest();
        await this.initPaymentRequestListeners();
        await this.initPaymentRequestButton();
    }
    /**
     * Initialized the `PaymentRequest` object.
     */
    async initPaymentRequest() {
        if (!this.stripe)
            return;
        const stripePaymentRequestOptions = this.getStripePaymentRequestOptions();
        const paymentRequest = this.stripe.paymentRequest(stripePaymentRequestOptions);
        const canMakePayment = await paymentRequest.canMakePayment();
        await this.setReadOnlyProperties({ paymentRequest, canMakePayment });
        if (!this.canMakePayment)
            this.fire('unsupported');
    }
    /**
     * Creates Stripe Payment Request Element.
     */
    async initPaymentRequestButton() {
        const { buttonTheme: theme, buttonType: type, canMakePayment, paymentRequest } = this;
        if (!canMakePayment)
            return;
        const computedStyle = window.ShadyCSS ? undefined : getComputedStyle(this);
        const propertyName = '--stripe-payment-request-button-height';
        const height = this.getCSSCustomPropertyValue(propertyName, computedStyle) || '40px';
        const style = { paymentRequestButton: { height, theme, type } };
        const options = { paymentRequest, style };
        const element = this.elements.create('paymentRequestButton', options);
        await this.setReadOnlyProperties({ element });
    }
    /**
     * Attaches listeners to the `PaymentRequest` object.
     */
    async initPaymentRequestListeners() {
        if (!this.canMakePayment)
            return;
        // @ts-expect-error: the types are incomplete in @types/stripe.js
        this.paymentRequest.on('click', this.updatePaymentRequest);
        this.paymentRequest.on('cancel', this.onCancel);
        this.paymentRequest.on('shippingaddresschange', this.onShippingaddresschange);
        this.paymentRequest.on('shippingoptionchange', this.onShippingoptionchange);
        switch (this.generate) {
            case 'payment-method':
                this.paymentRequest.on('paymentmethod', this.onPaymentResponse);
                break;
            case 'source':
                this.paymentRequest.on('source', this.onPaymentResponse);
                break;
            case 'token':
                this.paymentRequest.on('token', this.onPaymentResponse);
                break;
        }
    }
    /**
     * Updates the PaymentRequests's values
     */
    async updatePaymentRequest() {
        if (!this.paymentRequest)
            return;
        const { currency, total, displayItems, shippingOptions } = this.getStripePaymentRequestOptions();
        this.paymentRequest.update({ currency, total, displayItems, shippingOptions });
    }
    /**
     * Handle a `cancel` event
     */
    onCancel() {
        this.fire("cancel" /* cancel */);
    }
    /**
     * Completes the PaymentRequest.
     */
    async complete(paymentResponse, confirmationError) {
        const { error: paymentResponseError = null } = { ...paymentResponse };
        const status = (paymentResponseError || confirmationError) ? "fail" /* fail */ : "success" /* success */;
        paymentResponse.complete(status);
        this.fire(status, paymentResponse);
        return confirmationError ? { error: confirmationError } : paymentResponse;
    }
    /**
     * Handle a paymentResponse from Stripe
     */
    async onPaymentResponse(paymentResponse) {
        const { clientSecret, confirmPaymentIntent, complete } = this;
        const { error = null, paymentMethod = null, source = null, token = null, } = { ...paymentResponse };
        await this.setReadOnlyProperties({ error, paymentMethod, source, token });
        const isPaymentIntent = clientSecret && !error;
        if (isPaymentIntent)
            confirmPaymentIntent(paymentResponse);
        else
            complete(paymentResponse);
    }
    /**
     * When a PaymentIntent client secret is set, confirm the payment
     */
    async confirmPaymentIntent(paymentResponse) {
        const confirmCardData = { payment_method: this.paymentMethod.id };
        const { error = null, paymentIntent = null } = await this.confirmCardPayment(confirmCardData, { handleActions: false })
            .then(({ error: confirmationError }) => this.complete(paymentResponse, confirmationError)) // throws if first confirm errors
            .then(throwResponseError)
            .then(() => this.confirmCardPayment())
            .then(throwResponseError)
            .catch(error => ({ error })); // catch error from first confirm
        await this.setReadOnlyProperties({ error, paymentIntent });
    }
    /**
     * Stripe confirmCardPayment method
     */
    async confirmCardPayment(data, options) {
        return this.stripe.confirmCardPayment(this.clientSecret, data, options);
    }
    onShippingaddresschange(originalEvent) {
        this.fire('shippingaddresschange', originalEvent);
    }
    onShippingoptionchange(originalEvent) {
        this.fire('shippingoptionchange', originalEvent);
    }
    parseDatasets(selector) {
        const elements = [...this.querySelectorAll(selector)];
        return (!elements.length ? []
            : elements.map(mapDataset));
    }
};
_StripePaymentRequest_displayItems = new WeakMap(), _StripePaymentRequest_shippingOptions = new WeakMap();
StripePaymentRequest.is = 'stripe-payment-request';
StripePaymentRequest.styles = [
    styles$1,
    styles,
];
__decorate([
    slot,
    __metadata("design:type", String)
], StripePaymentRequest.prototype, "slotName", void 0);
__decorate([
    property({ type: Number, reflect: true }),
    __metadata("design:type", Number)
], StripePaymentRequest.prototype, "amount", void 0);
__decorate([
    property({
        type: Boolean,
        attribute: 'can-make-payment',
        reflect: true,
        readOnly: true,
        notify: true,
    }),
    __metadata("design:type", Object)
], StripePaymentRequest.prototype, "canMakePayment", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", String)
], StripePaymentRequest.prototype, "country", void 0);
__decorate([
    property({ type: String }),
    __metadata("design:type", Object)
], StripePaymentRequest.prototype, "currency", void 0);
__decorate([
    property({ type: Array }),
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [Object])
], StripePaymentRequest.prototype, "displayItems", null);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", String)
], StripePaymentRequest.prototype, "label", void 0);
__decorate([
    property({ type: Object, notify: true, readOnly: true, attribute: 'payment-intent' }),
    __metadata("design:type", Object)
], StripePaymentRequest.prototype, "paymentIntent", void 0);
__decorate([
    property({ type: Object, attribute: 'payment-request', readOnly: true }),
    __metadata("design:type", Object)
], StripePaymentRequest.prototype, "paymentRequest", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], StripePaymentRequest.prototype, "pending", void 0);
__decorate([
    property({ type: Boolean, attribute: 'request-payer-email' }),
    __metadata("design:type", Boolean)
], StripePaymentRequest.prototype, "requestPayerEmail", void 0);
__decorate([
    property({ type: Boolean, attribute: 'request-payer-name' }),
    __metadata("design:type", Boolean)
], StripePaymentRequest.prototype, "requestPayerName", void 0);
__decorate([
    property({ type: Boolean, attribute: 'request-payer-phone' }),
    __metadata("design:type", Boolean)
], StripePaymentRequest.prototype, "requestPayerPhone", void 0);
__decorate([
    property({ type: Boolean, attribute: 'request-shipping' }),
    __metadata("design:type", Boolean)
], StripePaymentRequest.prototype, "requestShipping", void 0);
__decorate([
    property({ type: Array }),
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [Object])
], StripePaymentRequest.prototype, "shippingOptions", null);
__decorate([
    property({ type: String, attribute: 'button-type' }),
    __metadata("design:type", Object)
], StripePaymentRequest.prototype, "buttonType", void 0);
__decorate([
    property({ type: String, attribute: 'button-theme' }),
    __metadata("design:type", Object)
], StripePaymentRequest.prototype, "buttonTheme", void 0);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], StripePaymentRequest.prototype, "onCancel", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", Promise)
], StripePaymentRequest.prototype, "complete", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], StripePaymentRequest.prototype, "onPaymentResponse", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Promise)
], StripePaymentRequest.prototype, "confirmPaymentIntent", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", Promise)
], StripePaymentRequest.prototype, "confirmCardPayment", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], StripePaymentRequest.prototype, "onShippingaddresschange", null);
__decorate([
    bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], StripePaymentRequest.prototype, "onShippingoptionchange", null);
StripePaymentRequest = __decorate([
    customElement('stripe-payment-request')
], StripePaymentRequest);
/**
 * Allows narrowing class field type
 * @see https://dev.to/bennypowers/narrowing-the-type-of-class-accessors-bi8
 */
function slot(proto, key) {
    Object.defineProperty(proto, key, {
        get() { return "stripe-payment-request-slot" /* 'stripe-payment-request' */; },
    });
}

export { StripePaymentRequest, isStripeDisplayItem, isStripeShippingOption };
//# sourceMappingURL=stripe-payment-request.js.map
